/**
 * @fileoverview release-event-map.ts — friendly names for release.* events.
 * @module apps/network/consumer/.../release-event-map
 *
 * Every server-emitted release.*, gates.*, traffic.* event the deployment
 * panel renders runs through this map first. Goal: speak to humans, not
 * cite enum codes.
 *
 * The deployment panel uses this for:
 *   - the live activity card ("Creating Fly machine…")
 *   - the per-event history rows ("Built image · docker.io/caddy:2-alpine")
 *   - the deploy-progress stage labels
 *
 * Two layers of mapping:
 *   1. STAGE: derived from `event.op` (release.previewing → "Preview",
 *      release.running → "Provisioning", etc.). One stage per logical
 *      sub-phase of release.run.
 *   2. EVENT: derived from `event.op` + `event.code` (release.running.MACHINE_CREATED →
 *      "Machine created"). Specific enough to read; light enough to scan.
 *
 * When an event op doesn't match any known stage, it falls through to the
 * raw code so we never silently drop information.
 */

import type { DeploymentStreamEvent } from "../../../../global/io/nodes/deployment/deployment-io.js";

/** Logical sub-stages of a release.run from server's `op` field. Ordered. */
export const RELEASE_STAGES = [
  "previewing",
  "gated",
  "running",
  "cutover",
  "drained",
  "succeeded",
] as const;
export type ReleaseStageId = (typeof RELEASE_STAGES)[number];

/** Friendly labels for each sub-stage. Title-case; what the user reads. */
export const STAGE_LABEL: Readonly<Record<ReleaseStageId, string>> = {
  previewing: "Preview",
  gated: "Gates",
  running: "Provisioning",
  cutover: "Cutover",
  drained: "Drain",
  succeeded: "Finalize",
};

/** One-sentence description for the active-stage card. */
export const STAGE_HINT: Readonly<Record<ReleaseStageId, string>> = {
  previewing: "Resolving image, building and signing the artifact.",
  gated: "Running the 5 gates (health · approval · attestation · capacity · budget).",
  running: "Provisioning the Fly machine and waiting for it to come up healthy.",
  cutover: "Switching traffic to the new release.",
  drained: "Draining in-flight requests from the previous machine.",
  succeeded: "Saving a restore point and reporting usage.",
};

/** Light icon character for each stage — keeps the visual atom dependency-free. */
export const STAGE_GLYPH: Readonly<Record<ReleaseStageId, string>> = {
  previewing: "▣",
  gated: "◆",
  running: "▶",
  cutover: "⇄",
  drained: "↓",
  succeeded: "✓",
};

/** Extract the stage id from an event op. Returns null if the op isn't a
 *  release substage. */
export function stageFromOp(op: string): ReleaseStageId | null {
  if (!op.startsWith("release.")) return null;
  const rest = op.slice("release.".length);
  if ((RELEASE_STAGES as readonly string[]).includes(rest)) {
    return rest as ReleaseStageId;
  }
  return null;
}

/** Concrete (op, code) → friendly event description. Used by both the live
 *  activity card and the history rows. The first match wins; specific
 *  entries before generic. */
interface FriendlyEvent {
  readonly label: string;
  readonly description?: (detail: unknown) => string | null;
  readonly tone: "ok" | "warn" | "info" | "error";
}

/** Pull a string from a {detail: object} payload safely. */
function fromDetail<K extends string>(detail: unknown, key: K): string | null {
  if (detail && typeof detail === "object" && key in detail) {
    const v = (detail as Record<string, unknown>)[key];
    return typeof v === "string" ? v : null;
  }
  return null;
}

const FRIENDLY: Readonly<Record<string, FriendlyEvent>> = {
  // ── release.previewing ───────────────────────────────────────────
  "release.previewing.START": {
    label: "Preview started",
    description: (d) => fromDetail(d, "targetId") ? `target ${fromDetail(d, "targetId")}` : null,
    tone: "info",
  },
  "release.previewing.SOURCE_DEFERRED": {
    label: "Source ingest deferred",
    description: () => "no Gitea source pinned; using the configured deploy image directly",
    tone: "info",
  },
  "release.previewing.NO_IMAGE": {
    label: "No deploy image configured",
    description: () => "the project has no deployImage set — open Settings to configure",
    tone: "error",
  },
  "release.previewing.BUILT": {
    label: "Image built",
    description: (d) => fromDetail(d, "image"),
    tone: "ok",
  },
  "release.previewing.SIGNED": {
    label: "Artifact signed",
    description: () => "supply-chain attestation captured",
    tone: "ok",
  },

  // ── release.gated ────────────────────────────────────────────────
  "release.gated.OK": {
    label: "Gates passed",
    description: () => "health · approval · attestation · capacity · budget — all pass",
    tone: "ok",
  },

  // ── release.running ──────────────────────────────────────────────
  "release.running.START": {
    label: "Provisioning started",
    description: (d) => {
      const region = fromDetail(d, "region");
      const image = fromDetail(d, "image");
      if (region && image) return `${region} · ${image}`;
      return image ?? region;
    },
    tone: "info",
  },
  "release.running.NO_FLY_TOKEN": {
    label: "Fly token missing",
    description: () => "FLY_API_TOKEN not bound — provisioning cannot continue",
    tone: "error",
  },
  "release.running.PROVISIONING_APP": {
    label: "Creating Fly app",
    description: (d) => fromDetail(d, "appName"),
    tone: "info",
  },
  "release.running.APP_PROVISIONED": {
    label: "Fly app ready",
    description: (d) => fromDetail(d, "appName"),
    tone: "ok",
  },
  "release.running.ALLOCATING_IPS": {
    label: "Allocating IPs",
    description: (d) => fromDetail(d, "appName"),
    tone: "info",
  },
  "release.running.IPS_ALLOCATED": {
    label: "IPs allocated",
    description: (d) => {
      if (!d || typeof d !== "object") return null;
      const ip4 = (d as Record<string, unknown>).ip4 as string | undefined;
      const ip6 = (d as Record<string, unknown>).ip6 as string | undefined;
      if (ip4 && ip6) return `${ip4} · ${ip6}`;
      return ip4 ?? ip6 ?? null;
    },
    tone: "ok",
  },
  "release.running.IP_ALLOCATE_FAILED": {
    label: "IP allocation failed",
    description: (d) => fromDetail(d, "error"),
    tone: "error",
  },
  "release.running.MACHINE_CREATED": {
    label: "Machine created",
    description: (d) => {
      if (!d || typeof d !== "object") return null;
      const ids = (d as Record<string, unknown>).machineIds;
      if (Array.isArray(ids) && ids.length > 0) return ids.map((x) => String(x).slice(0, 8)).join(", ");
      return null;
    },
    tone: "ok",
  },
  "release.running.HEALTHY": {
    label: "Machine healthy",
    description: () => "Fly's auto-checks reported a green machine",
    tone: "ok",
  },
  "release.running.DEPLOY_FAILED": {
    label: "Deploy failed",
    description: (d) => fromDetail(d, "error"),
    tone: "error",
  },

  // ── release.cutover ──────────────────────────────────────────────
  "release.cutover.START": {
    label: "Cutover started",
    tone: "info",
  },
  "release.cutover.OK": {
    label: "Cutover complete",
    description: () => "traffic now serving from the new machine",
    tone: "ok",
  },

  // ── release.drained ──────────────────────────────────────────────
  "release.drained.OK": {
    label: "Old machine drained",
    description: () => "previous machine retired",
    tone: "ok",
  },

  // ── release.succeeded ────────────────────────────────────────────
  "release.succeeded.RESTORE_POINT_SAVED": {
    label: "Restore point saved",
    description: (d) => fromDetail(d, "id"),
    tone: "ok",
  },
  "release.succeeded.RESTORE_POINT_SKIPPED": {
    label: "Restore point skipped",
    description: (d) => fromDetail(d, "reason"),
    tone: "warn",
  },
  "release.succeeded.USAGE_REPORTED": {
    label: "Usage reported",
    description: (d) => {
      const metric = fromDetail(d, "metric");
      const qty = (d as Record<string, unknown> | null)?.["quantity"];
      if (metric && typeof qty === "number") return `${metric} · ${qty}`;
      return metric;
    },
    tone: "info",
  },
  "release.succeeded.OK": {
    label: "Deploy succeeded",
    description: (d) => fromDetail(d, "machineId") ?? fromDetail(d, "appName"),
    tone: "ok",
  },

  // ── Cutover dispatch (separate manual action — not a release substage) ──
  "cutover.run.OK": {
    label: "Cutover dispatched",
    tone: "ok",
  },

  // ── Rollback ─────────────────────────────────────────────────────
  "rollback.restoreToPoint.OK": {
    label: "Rolled back",
    description: (d) => fromDetail(d, "id"),
    tone: "warn",
  },
  "rollback.restoreToPoint.ALIAS_TO_RESTORE": {
    label: "Supply-chain rollback aliased",
    description: () => "for BYO-image M1, rollback.runSupplyChain aliases to restoreToPoint",
    tone: "info",
  },
};

/** Resolve a friendly description for an event. Falls back to a synthetic
 *  label built from op + code when nothing matches. */
export function friendlyOf(event: DeploymentStreamEvent): {
  label: string;
  description: string | null;
  tone: "ok" | "warn" | "info" | "error";
} {
  const key = `${event.op}.${event.code}`;
  const hit = FRIENDLY[key];
  if (hit) {
    return {
      label: hit.label,
      description: hit.description?.(event.detail) ?? null,
      tone: hit.tone,
    };
  }
  // Fallback: synthesize from op + code.
  const stage = stageFromOp(event.op);
  const stageBit = stage ? `${STAGE_LABEL[stage]}: ` : "";
  return {
    label: `${stageBit}${event.code}`,
    description: null,
    tone:
      event.origin === "ok"
        ? "ok"
        : event.origin === "runtime" || event.origin === "domain"
          ? "error"
          : "info",
  };
}

/** Given a list of release.* events from a deploy, compute the active
 *  stage and an estimated step number (0-based) within RELEASE_STAGES.
 *  Returns null when there are no events. */
export function activeStageFrom(
  events: readonly DeploymentStreamEvent[],
): { stage: ReleaseStageId; stepIndex: number } | null {
  let latestStage: ReleaseStageId | null = null;
  for (const e of events) {
    const s = stageFromOp(e.op);
    if (s) latestStage = s;
  }
  if (!latestStage) return null;
  return { stage: latestStage, stepIndex: RELEASE_STAGES.indexOf(latestStage) };
}

/** Returns true iff the deploy reached `release.succeeded` AND no event
 *  with a non-`ok` origin appeared later. */
export function isDeploySucceeded(events: readonly DeploymentStreamEvent[]): boolean {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (stageFromOp(e.op) === "succeeded" && e.origin === "ok") return true;
    if (e.origin === "runtime" || e.origin === "domain") return false;
  }
  return false;
}

/** Returns the most recent non-ok origin event if the deploy hit a failure. */
export function deployFailure(
  events: readonly DeploymentStreamEvent[],
): DeploymentStreamEvent | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.origin === "runtime" || e.origin === "domain") return e;
    if (stageFromOp(e.op) === "succeeded" && e.origin === "ok") return null;
  }
  return null;
}
